import { Button } from '@playcanvas/pcui/react';
import { UsdzExporter, Vec3 } from 'playcanvas';
import React from 'react';
import * as pc from 'playcanvas';

import AnimationControls from './animation-controls';
import { CameraPanel, SkyboxPanel, LightPanel, DebugPanel, ViewPanel, ViewPositionPanel } from './panels';
import { addEventListenerOnClickOnly } from '../../helpers';
import { t } from '../../translation';
import { SetProperty, ObserverData } from '../../types';
import ImageGallery from '../components/image-gallery';


const PopupPanelControls = (props: { observerData: ObserverData, setProperty: SetProperty }) => {
    return (<>
        <CameraPanel setProperty={props.setProperty} observerData={props.observerData} />
        <SkyboxPanel setProperty={props.setProperty} skyboxData={props.observerData.skybox} uiData={props.observerData.ui} />
        <LightPanel setProperty={props.setProperty} lightData={props.observerData.light} uiData={props.observerData.ui} shadowCatcherData={props.observerData.shadowCatcher} />
        <DebugPanel setProperty={props.setProperty} debugData={props.observerData.debug} uiData={props.observerData.ui} />
        <ViewPanel setProperty={props.setProperty} sceneData={props.observerData.scene} uiData={props.observerData.ui} runtimeData={props.observerData.runtime} />
        <ViewPositionPanel setProperty={props.setProperty} uiData={props.observerData.ui} />
    </>);
};

class PopupButtonControls extends React.Component<{ observerData: ObserverData, setProperty: SetProperty, userMode?: string }> {
    popupPanelElement: any;


    takeScreenShot = () => {
        if (window.viewer) {
            try {
                // Ensure the scene is rendered before taking screenshot
                window.viewer.renderNextFrame();

                // Wait a moment for the render to complete, then capture
                setTimeout(() => {
                    window.viewer.downloadPngScreenshot();
                    //console.log('Screenshot captured and download initiated');
                }, 100);
            } catch (error) {
                console.error('Error taking screenshot:', error);
                alert(t('error.screenshot'));
            }
        } else {
            console.error('Viewer not available');
            alert(t('error.noViewer'));
        }
    }

    render() {
        let removeDeselectEvents: any;
        const handleClick = (value: string) => {
            this.props.setProperty('ui.active', this.props.observerData.ui.active === value ? null : value);

            // after a popup button is set active, listen for another click outside the panel to deactivate it
            if (!this.popupPanelElement) this.popupPanelElement = document.getElementById('popup');
            // add the event listener after the current click is complete
            setTimeout(() => {
                if (removeDeselectEvents) removeDeselectEvents();
                const deactivateUi = (e: any) => {
                    if (this.popupPanelElement.contains(e.target)) {
                        return;
                    }
                    this.props.setProperty('ui.active', null);
                    removeDeselectEvents();
                    removeDeselectEvents = null;
                };
                removeDeselectEvents = addEventListenerOnClickOnly(document.body, deactivateUi, 4);
            });
        };



        const buildClass = (value: string) => {
            return (this.props.observerData.ui.active === value) ? ['popup-button', 'selected'] : ['popup-button'];
        };

        const buildClassScreenshot = (value: string) => {
            return (this.props.observerData.ui.active === value) ? ['screenshot-button', 'selected'] : ['screenshot-button'];
        };

        const { userMode } = this.props;

        return (
            <div id='popup-buttons-parent'>
                <AnimationControls animationData={this.props.observerData.animation} setProperty={this.props.setProperty} />

                {/* Ocultar botones en modo editor */}
                {userMode === 'admin' && <div title={t('panel.camera')}><Button id='camera-admin' class={[...buildClass('camera'), 'large-icon']} icon='E212' width={50} height={50} onClick={() => handleClick('camera')} /></div>}
                {userMode === 'editor' && <div title={t('panel.skybox')}><Button id='skybox-editor' class={[...buildClass('skybox'), 'large-icon']} icon='E200' width={50} height={50} onClick={() => handleClick('skybox')} /></div>}
                {userMode === 'admin' && <div title={t('panel.light')}><Button id='light-admin' class={[...buildClass('light'), 'large-icon']} icon='E194' width={50} height={50} onClick={() => handleClick('light')} /></div>}
                {userMode === 'admin' && <div title={t('panel.debug')}><Button id='debug-admin' class={[...buildClass('debug'), 'large-icon']} icon='E134' width={50} height={50} onClick={() => handleClick('debug')} /></div>}
                {(userMode === 'editor' || userMode === 'admin') && <div title={t('panel.view')}><Button id='view-editor' class={[...buildClass('view'), 'large-icon']} icon='E301' width={50} height={50} onClick={() => handleClick('view')} /></div>}
                {/* Screenshot button - moved to RightPanel toolbar */}
                {/* <div title={t('app.takeScreenshot')}>
                    <Button class={[...buildClassScreenshot('screenshot'), 'large-icon']} icon='E397' width={50} height={50} onClick={() => this.takeScreenShot()} />
                </div> */}
            </div>
        );
    }
}


class PositionPanel extends React.Component<{ observerData: ObserverData, setProperty: SetProperty }> {
    viewPositionPanelElement: any;



    setCameraView = (viewType: string) => {
        if (!window.viewer) return;

        const viewer = window.viewer;
        const multiCamera = viewer.multiCamera;
        const sceneBounds = viewer.sceneBounds || viewer.dynamicSceneBounds;

        if (!sceneBounds || !sceneBounds.center) return;

        const distance = 8;
        multiCamera.sceneSize = distance * 0.5;

        // The PLY model's front faces +Z after levelPlyModel (Y-rotation 180°).
        // We ONLY move the viewer camera – the PLY entity is never rotated here,
        // so camera-position 3D models always stay correctly placed in world space.
        const d45 = distance / Math.sqrt(2);
        const focusPosition = new Vec3(0, 0, 0);
        let targetPosition: Vec3;

        switch (viewType) {
            case 'front':      targetPosition = new Vec3(0,    0.3,  distance); break;
            case 'frontRight': targetPosition = new Vec3(d45,  0.3,  d45);      break;
            case 'right':      targetPosition = new Vec3(distance, 0, 0);       break;
            case 'rightBack':  targetPosition = new Vec3(d45,  0.3, -d45);      break;
            case 'behind':     targetPosition = new Vec3(0,    0.3, -distance); break;
            case 'backLeft':   targetPosition = new Vec3(-d45, 0.3, -d45);      break;
            case 'left':       targetPosition = new Vec3(-distance, 0, 0);      break;
            case 'leftFront':  targetPosition = new Vec3(-d45, 0.3,  d45);      break;
            case 'top':        targetPosition = new Vec3(0,    distance, 0);    break;
            default: return;
        }

        // multiCamera.focus() computes elevation as atan2(y, z), which breaks when
        // the target has a negative Z component (back views) – it returns ~±180° and
        // flips the entire scene. We bypass focus() and set the orbit state directly
        // using the geometrically-correct elevation formula (angle from the XZ plane).
        const dir = new Vec3();
        dir.sub2(targetPosition, focusPosition);
        const hDist = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
        const elevDeg = Math.atan2(dir.y, hDist) * (180 / Math.PI);
        const azimDeg = Math.atan2(dir.x, dir.z) * (180 / Math.PI);

        const mc = multiCamera as any;
        mc._origin.copy(focusPosition);
        mc._position.copy(focusPosition);           // skip smoothMove lerp → immediate
        mc._dir.set(-elevDeg, -azimDeg);
        mc._angles.set(-elevDeg, -azimDeg, 0);     // skip smoothLook lerp → immediate
        mc._zoomDist = distance;

        viewer.renderNextFrame();
    };

    urlCheckerButtons = () => {
        const currentUrl = window.location.href;
        let toolsActive: boolean;

        if (currentUrl.includes('toolsButtons')) {
            toolsActive = true;
        } else {
            toolsActive = false;
        }
        return toolsActive;
    }
    render() {

        const buildClass = (value: string) => {
            return (this.props.observerData.ui.active === value) ? ['position-button-views', 'selected'] : ['position-button-views'];
        };


            return (
                <div id='position-buttons-parent'>
                    <Button class={buildClass('leftFront')} text={t('position.leftFront')} width={80} height={40} onClick={() => this.setCameraView('leftFront')} />
                    <Button class={buildClass('front')} text={t('position.front')} width={80} height={40} onClick={() => this.setCameraView('front')} />
                    <Button class={buildClass('frontRight')} text={t('position.frontRight')} width={80} height={40} onClick={() => this.setCameraView('frontRight')} />
                    
                    <Button class={buildClass('left')} text={t('position.left')} width={80} height={40} onClick={() => this.setCameraView('left')} />
                    {/* Center placeholder - no button */}
                    <div style={{width: '80px', height: '40px'}}></div>
                    <Button class={buildClass('right')} text={t('position.right')} width={80} height={40} onClick={() => this.setCameraView('right')} />
                    
                    <Button class={buildClass('backLeft')} text={t('position.backLeft')} width={80} height={40} onClick={() => this.setCameraView('backLeft')} />
                    <Button class={buildClass('behind')} text={t('position.back')} width={80} height={40} onClick={() => this.setCameraView('behind')} />
                    <Button class={buildClass('rightBack')} text={t('position.rightBack')} width={80} height={40} onClick={() => this.setCameraView('rightBack')} />
                    {/* <Button class={buildClass('top')} text={t('position.top')} width={100} height={40} onClick={() => this.setCameraView('top')} /> */}
                </div>
            );
        

    }
}

const toggleCollapsed = () => {
    document.getElementById('panel-left').classList.toggle('collapsed');
};


class SyncVariables {
    ZoomSync: number;

    constructor() {
        this.ZoomSync = 8; // Valor inicial predeterminado
    }
    syncZoom(zoomValue: number) {
        this.ZoomSync = zoomValue;

    }
}

class OrbitSliders extends React.Component<{ observerData: ObserverData, setProperty: SetProperty }> {
    viewPositionPanelElement: any;
    private originalPlyRotation: { x: number, y: number, z: number } | null = null;
    private initialDistance = 8;
    private initialModelHeight = 0;
    private minZoom = 3;
    private maxZoom = 15;
    private currentLift = 0;
    private maxLift = 1;
    private minLift = -1;
    private baseOrigin: any = null;
    private baseLiftOriginY: number | null = null;

    instSync = new SyncVariables();

    state = {
        zoomValue: this.initialDistance,
        liftValue: 50, // 50 is middle position (0 height)
        panHorizontal: 0,   // Horizontal pan (left-right displacement)
        orbitalEnabled: false
    };

    componentDidMount() {
        this.syncOrbitalMode();
        window.addEventListener('orbital-controls-changed', this.handleOrbitalControlsChanged as EventListener);
    }

    componentWillUnmount() {
        window.removeEventListener('orbital-controls-changed', this.handleOrbitalControlsChanged as EventListener);
    }

    private syncOrbitalMode = () => {
        const enabled = Boolean(window.viewer?.enableOrbitalControls);
        this.setState({ orbitalEnabled: enabled });
    };

    private handleOrbitalControlsChanged = (event: Event) => {
        const customEvent = event as CustomEvent<{ enabled?: boolean }>;
        const enabled = customEvent?.detail?.enabled;
        this.setState({ orbitalEnabled: typeof enabled === 'boolean' ? enabled : Boolean(window.viewer?.enableOrbitalControls) });
    };

    public handleZoomChange = (value: number) => {
        this.setState({ zoomValue: value });
        this.updateCamera();
    };

    // Handle horizontal pan (displacement left-right)
    handlePanHorizontal = (value: number) => {
        this.setState({ panHorizontal: value });
        this.updatePan();
    };

    // Update horizontal pan - move camera and focus point left/right
    updatePan = () => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;
            const camera = viewer.camera.camera;

            // Get camera's right vector for horizontal movement
            const right = camera.entity.right.clone();
            
            // Calculate pan offset based on slider value
            // Value range: -5 to 5 (pan distance in scene units)
            const panDistance = (this.state.panHorizontal / 100) * 5;
            
            // Apply horizontal displacement to the origin (focus point)
            const offset = right.mulScalar(panDistance);
            
            if (multiCamera._origin) {
                // Store base origin if not stored
                if (!this.baseOrigin) {
                    this.baseOrigin = multiCamera._origin.clone();
                }
                
                // Apply offset from base origin
                multiCamera._origin.copy(this.baseOrigin);
                multiCamera._origin.add(offset);
            }

            viewer.renderNextFrame();
        }
    };

    // Handle the lift of the vehicle model using a new slider
    handleModelLift = (value: number) => {
        // Convert slider value (0-100) to height range (minLift to maxLift)
        // Inverted: value 0 maps to maxLift, value 100 maps to minLift
        const range = this.maxLift - this.minLift;
        this.currentLift = this.minLift + ((100 - value) / 100) * range;
        this.setState({ liftValue: value });
        this.updateModelLift();
    };

    updateModelLift = () => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;

            if (multiCamera._origin) {
                // Store the orbit focus Y the first time lift is applied
                if (this.baseLiftOriginY === null) {
                    this.baseLiftOriginY = multiCamera._origin.y;
                }
                // Move the orbit focus DOWN so the model appears raised in the viewport,
                // matching the visual behaviour of physically lifting the scene.
                // This never moves any scene entity, so spawned 3D models stay fixed.
                multiCamera._origin.y = this.baseLiftOriginY - this.currentLift;
            }

            viewer.renderNextFrame();
        }
    };

    updateCamera = () => {
        if (window.viewer) {
            const viewer = window.viewer;
            const multiCamera = viewer.multiCamera;

            // Calculate new distance based on zoom value
            const newDistance = this.maxZoom - (this.state.zoomValue - this.minZoom);
            
            // Update scene size for proper framing
            multiCamera.sceneSize = newDistance * 0.5;
            
            // Directly set the zoom distance - multiCamera will handle the rest
            // This maintains the current camera orientation while adjusting distance
            multiCamera._zoomDist = newDistance;

            viewer.renderNextFrame();
        }
    };

    isMobileDevice() {
        let isMobile: boolean;
        let agent = navigator.userAgent;
        //console.log("User-agent:<br>" + agent);
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            // true for mobile device
            isMobile = true;
        } else {
            // false for not mobile device
            isMobile = false;
        }
        return isMobile;
    }

    mobileChecker = () => {
        if (!this.isMobileDevice()) {
            return (
                <div id="orbit-sliders-container" style={{
                    position: 'absolute',
                    top: '30px',
                    left: '25%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '20px',
                    zIndex: 100
                }}>
                    {/* Left side: Zoom and Pan sliders stacked vertically */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Zoom Slider */}
                        <div style={{ width: '200px', background: 'rgba(30, 30, 30, 0.7)', padding: '8px 12px', borderRadius: '5px' }}>
                            <div style={{ marginBottom: '5px', color: 'white', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                                {t('position.zoom')}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: 'white', fontSize: '10px', marginRight: '5px' }}>-</span>
                                <input
                                    type="range"
                                    min={this.minZoom}
                                    max={this.maxZoom}
                                    step={0.1}
                                    value={this.state.zoomValue}
                                    onChange={(e) => this.handleZoomChange(parseFloat(e.target.value))}
                                    style={{ width: '100%', height: '20px' }}
                                />
                                <span style={{ color: 'white', fontSize: '10px', marginLeft: '5px' }}>+</span>
                            </div>
                        </div>

                        {/* Horizontal Pan Slider */}
                        <div style={{ width: '200px', background: 'rgba(30, 30, 30, 0.7)', padding: '8px 12px', borderRadius: '5px' }}>
                            <div style={{ marginBottom: '5px', color: 'white', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                                {t('position.panHorizontal')}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: 'white', fontSize: '10px', marginRight: '5px' }}>←</span>
                                <input
                                    type="range"
                                    min={-100}
                                    max={100}
                                    step={1}
                                    value={this.state.panHorizontal}
                                    onChange={(e) => this.handlePanHorizontal(parseFloat(e.target.value))}
                                    style={{ width: '100%', height: '20px' }}
                                />
                                <span style={{ color: 'white', fontSize: '10px', marginLeft: '5px' }}>→</span>
                            </div>
                        </div>
                    </div>

                    {/* Lift Slider (Vertical) */}
                    <div style={{ height: '150px', background: 'rgba(30, 30, 30, 0.7)', padding: '8px 12px', borderRadius: '5px' }}>
                        <div style={{ marginBottom: '5px', color: 'white', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                            {t('position.lift')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px' }}>
                            <span style={{ color: 'white', fontSize: '10px', marginBottom: '5px' }}>+</span>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                step={0.5}
                                value={this.state.liftValue}
                                onChange={(e) => this.handleModelLift(parseFloat(e.target.value))}
                                style={{
                                    width: '20px',
                                    height: '100px',
                                    margin: '0',
                                    writingMode: 'vertical-lr',
                                    direction: 'ltr',
                                    transform: 'rotate(180deg)',
                                }}
                            />
                            <span style={{ color: 'white', fontSize: '10px', marginTop: '5px' }}>-</span>
                        </div>
                    </div>
                </div>
            );
        }
    };

    render() {
        if (this.state.orbitalEnabled) {
            return null;
        } else {
            return (
                this.mobileChecker()
            );
        }
    }
}

class PositionSphereButtons extends React.Component<{ observerData: ObserverData, setProperty: SetProperty }> {

    state = {
        spheres: [] as Array<{ name: string, index: number, source: 'sphere' | 'camera' }>,
        selectedSphere: null as { name: string, description: string, coords: string, index: number, source: 'sphere' | 'camera' } | null,
        isListVisible: false,
        isPhotoGalleryOpen: false,
        photoGalleryMode: 'normal' as 'normal' | 'selector',
        damagePhotosToShow: [] as string[],
        isCreating: false,
        isEditing: false
    };

    private stateCheckInterval: any = null;

    componentDidMount() {
        // Expose this component to the viewer for programmatic selection
        if (window.viewer) {
            window.viewer.positionSphereButtonsComponent = this;
        }
        
        // Poll viewer state periodically to ensure UI stays in sync
        this.stateCheckInterval = setInterval(() => {
            if (window.viewer) {
                const viewerIsCreating = window.viewer.isCreating;
                const viewerIsEditing = window.viewer.isEditing;
                
                if (viewerIsCreating !== this.state.isCreating || viewerIsEditing !== this.state.isEditing) {
                    this.setState({
                        isCreating: viewerIsCreating,
                        isEditing: viewerIsEditing
                    });
                }
            }
        }, 100); // Check every 100ms
    }

    componentWillUnmount() {
        // Clean up the reference
        if (window.viewer && window.viewer.positionSphereButtonsComponent === this) {
            window.viewer.positionSphereButtonsComponent = null;
        }
        
        // Clear the interval
        if (this.stateCheckInterval) {
            clearInterval(this.stateCheckInterval);
        }
    }

    // Public methods to update creation/editing state
    public setCreatingState(isCreating: boolean) {
        this.setState({ isCreating });
    }

    public setEditingState(isEditing: boolean) {
        this.setState({ isEditing });
    }

    // Public method to select a sphere programmatically
    public selectSphereByName(sphereName: string, sphereIndex: number) {
        const selectedSphere = this.state.spheres.find(sphere => sphere.name === sphereName);
        
        if (selectedSphere && window.viewer) {
            const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
            const description = window.viewer.descriptionsPointer[sphereIndex] || 'No description';

            const selectedSphereEntity = allSpheres.find(sphere => sphere.entity.name === sphereName);
            if (selectedSphereEntity) {
                const selectedSpherePos = selectedSphereEntity.entity.getPosition();
                window.viewer.editingSpherePos = selectedSpherePos.clone();
                window.viewer.moveCameraToSphere(sphereIndex);
            }

            // Update the state with callback to ensure DOM is updated
            this.setState({
                selectedSphere: {
                    name: sphereName,
                    description: description,
                    index: sphereIndex,
                    source: 'sphere'
                }
            }, () => {
                // After state update, force the select element to update
                const sphereSelect = document.getElementById('sphere-select') as HTMLSelectElement;
                if (sphereSelect && sphereSelect.value !== sphereName) {
                    sphereSelect.value = sphereName;
                }
            });
        }
    }

    updateSphereList = () => {
        if (window.viewer && typeof window.viewer.selectedPointerSpawner === 'function') {
            const { nameSpheres } = window.viewer.selectedPointerSpawner();
            const sphereEntries = nameSpheres.map((name: string, index: number) => ({ name, index, source: 'sphere' as const }));

            const cameraEntries = (window.viewer.cameraDamageRecords || []).map((damage: any, index: number) => ({
                name: `[CAM] ${damage.name} - ${damage.selectValue || '-'}`,
                index,
                source: 'camera' as const
            }));

            const spheres = [...sphereEntries, ...cameraEntries];
            this.setState({ spheres });
        }
    }

    handleRefreshList = () => {
        // Only refresh the list without toggling visibility
        if (window.viewer && this.state.isListVisible) {
            window.viewer.selectedPointerSpawner();
            this.updateSphereList();

            if (this.state.selectedSphere?.source === 'camera') {
                const damage = window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index];
                const cameraIds = damage?.cameraIds || (damage?.cameraId ? [damage.cameraId] : []);
                window.viewer.highlightCameraDamageSelection?.(cameraIds);
                window.viewer.setCameraDamageNavigationFilter?.(cameraIds);
            }
        }
    }

    handleListClick = () => {
        if (window.viewer) {
            const editButton = document.getElementById('position-sphere-edit');

            if (this.state.isListVisible) {
                // Hide list and reset selection
                this.setState({
                    isListVisible: false,
                    selectedSphere: null
                });
                window.viewer.highlightCameraDamageSelection?.(null);
                window.viewer.clearCameraDamageNavigationFilter?.();
                // Hide all spheres in the scene
                this.toggleSpheresVisibility(false);
                // Reset camera focus to origin
                const multiCamera = window.viewer.multiCamera;
                if (multiCamera) {
                    multiCamera.focus(new Vec3(0, 0, 0));
                }
                if (editButton) editButton.style.display = 'none';
                window.viewer.renderNextFrame();
            } else {
                // Show list and update spheres
                window.viewer.selectedPointerSpawner();
                this.updateSphereList();
                this.setState({ isListVisible: true });
                // Show all spheres in the scene
                this.toggleSpheresVisibility(true);
                // Don't show info panel until a sphere is selected
                // positionSphereInfoPanel.style.display = 'block';
                if (editButton) editButton.style.display = 'inline-block';
                window.viewer.renderNextFrame();
            }
        }
    }

    toggleSpheresVisibility = (visible: boolean) => {
        if (window.viewer) {
            const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
            allSpheres.forEach((sphere: any) => {
                sphere.entity.enabled = visible;
            });
        }
    }

    // Photo gallery handlers
    handleOpenPhotoSelector = () => {
        // Recuperar fotos ya marcadas para el daño actual (para pre-selección, no filtrado)
        let existingPhotos: string[] = [];
        
        if (window.viewer) {
            // Usar los estados del viewer directamente para mayor confiabilidad
            if (window.viewer.isEditing && this.state.selectedSphere) {
                // Si está editando, obtener las fotos del daño seleccionado
                existingPhotos = window.viewer.getPhotosForDamage(this.state.selectedSphere.index);
            } else if (window.viewer.isCreating) {
                // Si está creando, obtener las fotos temporales
                existingPhotos = window.viewer.getTempPhotoIds();
            }
        }
        
        this.setState({
            isPhotoGalleryOpen: true,
            photoGalleryMode: 'selector',
            damagePhotosToShow: existingPhotos // Solo para pre-selección, no para filtrado
        });
    }

    handleViewDamagePhotos = () => {
        if (window.viewer && this.state.selectedSphere) {
            if (this.state.selectedSphere.source === 'camera') {
                const damage = window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index];
                const cameraIds = damage?.cameraIds || (damage?.cameraId ? [damage.cameraId] : []);
                if (cameraIds.length > 0 && (window as any).openDamageCameraCarousel) {
                    (window as any).openDamageCameraCarousel(cameraIds, damage?.id || '');
                    return;
                }
            }

            const photoIds = this.state.selectedSphere.source === 'camera'
                ? (window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index]?.photoIds || [])
                : window.viewer.getPhotosForDamage(this.state.selectedSphere.index);

            this.setState({
                isPhotoGalleryOpen: true,
                photoGalleryMode: 'normal',
                damagePhotosToShow: photoIds
            });
        }
    }

    handleClosePhotoGallery = () => {
        this.setState({
            isPhotoGalleryOpen: false,
            photoGalleryMode: 'normal',
            damagePhotosToShow: []
        });
    }

    handlePhotosSelected = (photoIds: string[]) => {
        if (window.viewer) {
            window.viewer.setTempPhotoIds(photoIds);
        }
        this.handleClosePhotoGallery();
    }

    handleOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // Handle option change in the select dropdown
        const selectedOption = event.target.value;
        if (!selectedOption) {
            if (window.viewer) {
                window.viewer.highlightCameraDamageSelection?.(null);
                window.viewer.clearCameraDamageNavigationFilter?.();
            }
            this.setState({ selectedSphere: null });
            return;
        }

        const selectedSphere = this.state.spheres.find(sphere => sphere.name === selectedOption);
        if (selectedSphere?.source === 'camera' && window.viewer) {
            const damage = window.viewer.cameraDamageRecords?.[selectedSphere.index];
            const cameraIds = damage?.cameraIds || (damage?.cameraId ? [damage.cameraId] : []);
            window.viewer.highlightCameraDamageSelection?.(cameraIds);
            window.viewer.setCameraDamageNavigationFilter?.(cameraIds);

            this.setState({
                selectedSphere: {
                    name: selectedOption,
                    description: damage?.description || 'No description',
                    index: selectedSphere.index,
                    source: 'camera'
                }
            });
            return;
        }

        const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
        if (allSpheres.length > 0) {
            if (selectedSphere && window.viewer) {
                window.viewer.highlightCameraDamageSelection?.(null);
                window.viewer.clearCameraDamageNavigationFilter?.();
                const index = selectedSphere.index;
                //console.log('Selected sphere index:', index);
                // Get description from viewer's descriptionsPointer array
                const description = window.viewer.descriptionsPointer[index] || 'No description';

                const selectedSphereEntity = allSpheres.find(sphere => sphere.entity.name === selectedOption);
                // Execute this block only once to avoid multiple calls
                if (selectedSphereEntity) {
                    const selectedSpherePos = selectedSphereEntity.entity.getPosition();
                    window.viewer.editingSpherePos = selectedSpherePos.clone();
                    //console.log('Selected sphere position:', selectedSpherePos);
                    window.viewer.moveCameraToSphere(index)
                }

                this.setState({
                    selectedSphere: {
                        name: selectedOption,
                        description: description,
                        index: index,
                        source: 'sphere'
                    }
                });
            }
        }
    }


    render() {
        // Ocultar panel de selección durante creación o edición
        // Usar tanto el estado local como el estado del viewer para mayor confiabilidad
        const viewerIsCreating = window.viewer?.isCreating || false;
        const viewerIsEditing = window.viewer?.isEditing || false;
        const isCreatingOrEditing = this.state.isCreating || this.state.isEditing || viewerIsCreating || viewerIsEditing;

        return (

            // Checkbox list of all the position spheres created
            <div id="markers-div">
                {/* Selector and info panel hidden - moved to DamageToggle in RightPanel */}
                <div id="position-sphere-checkbox-container" style={{ display: 'none' }}>
                    <div className={"sphere-selector"}>
                        <label htmlFor="sphere-select">{t('markers.selectDamage')}: </label>
                        <select
                            id="sphere-select"
                            value={this.state.selectedSphere?.name || ''}
                            onChange={this.handleOptionChange}
                            className="sphere-select">
                            <option value="">{t('markers.selectSphere')}</option>
                            {this.state.spheres.map((sphere) => (
                                <option key={`${sphere.source}-${sphere.index}`} value={sphere.name}>
                                    {sphere.name}
                                </option>
                            ))}
                        </select>

                        <button id="position-sphere-refresh" onClick={this.handleRefreshList} title={t('markers.refreshList')}>⟳</button>
                    </div>


                </div>
                <div id="position-sphere-infopanel" style={{ display: 'none' }}>
                    <table>
                        <tr>
                            <th>{t('markers.tableName')}</th>
                            <th>{t('markers.tableDescription')}</th>
                            <th>{t('markers.tableSide')}</th>
                            <th>{t('gallery.viewPhotos')}</th>
                        </tr>
                        <tr>
                            <td>{this.state.selectedSphere?.name || '-'}</td>
                            <td>{this.state.selectedSphere?.description || '-'}</td>
                            <td>{this.state.selectedSphere?.source === 'camera' ? (window.viewer?.cameraDamageRecords?.[this.state.selectedSphere?.index]?.selectValue || '-') : (window.viewer?.selectPointer[this.state.selectedSphere?.index] || '-')}</td>
                            <td>
                                {window.viewer && this.state.selectedSphere && (this.state.selectedSphere.source === 'camera' ? ((window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index]?.photoIds || []).length > 0) : (window.viewer.getPhotosForDamage(this.state.selectedSphere.index).length > 0)) ? (
                                    <button 
                                        onClick={this.handleViewDamagePhotos}
                                        style={{ 
                                            padding: '4px 8px', 
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {t('gallery.viewPhotos')} ({this.state.selectedSphere.source === 'camera' ? (window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index]?.photoIds || []).length : window.viewer.getPhotosForDamage(this.state.selectedSphere.index).length})
                                    </button>
                                ) : '-'}
                            </td>
                        </tr>
                    </table>

                </div>


                <div>
                    <div id="position-sphere-inputs-container">
                        <input type="text" id="sphere-name-input" placeholder={t('markers.sphereName')} style={{ display: 'none' }} required />
                        <select id="sphere-select-input" style={{ display: 'none' }} required>
                            <option value="">{t('markers.selectSide')}</option>
                            <option value="Front">{t('markers.sideFront')}</option>
                            <option value="Left">{t('markers.sideLeft')}</option>
                            <option value="Right">{t('markers.sideRight')}</option>
                            <option value="Back">{t('markers.sideBack')}</option>
                        </select>
                        <button 
                            id="sphere-select-photos-button" 
                            onClick={this.handleOpenPhotoSelector} 
                            style={{ display: 'none' }}
                            type="button"
                        >
                            {t('gallery.selectPhotos')}
                        </button>
                        <textarea id="sphere-description-input" placeholder={t('markers.sphereDescription')} style={{ display: 'none' }} required></textarea>
                    </div>
                </div>

                <div id="position-sphere-buttons-container" style={{ display: 'none' }}>
                    {/* Eye button hidden - functionality moved to DamageToggle in RightPanel */}
                    <button
                        id="position-sphere-list"
                        onClick={this.handleListClick}
                        style={{ display: 'none' }}
                        title={this.state.isListVisible ? t('markers.hide') : t('markers.list')}
                    />
                    {/* Edit button hidden - functionality moved to RightPanel damage section */}
                    <button id="position-sphere-edit" style={{ display: 'none' }} onClick={() => {
                        if (!window.viewer || !this.state.selectedSphere) {
                            return;
                        }

                        if (this.state.selectedSphere.source === 'camera') {
                            const damage = window.viewer.cameraDamageRecords?.[this.state.selectedSphere.index];
                            const cameraId = damage?.cameraIds?.[0] || damage?.cameraId || null;
                            if (cameraId) {
                                window.viewer.openCameraDamageEditor?.(cameraId);
                            }
                            return;
                        }

                        const sphereSelector = document.getElementById('sphere-select') as HTMLSelectElement;
                        if (sphereSelector) {
                            const syntheticEvent = { target: sphereSelector } as React.ChangeEvent<HTMLSelectElement>;
                            window.viewer.editPointerSpawner?.(syntheticEvent);
                        }
                    }}>{t('markers.edit')}</button>
                    {/* <button id="position-sphere-export" onClick={() => window.viewer?.saveAllPointers()}>{t('markers.export')}</button> */}
                    {/* <button id="position-sphere-import" onClick={() => window.viewer?.loadDamagesFromAPI()}>{t('markers.import')}</button> */}
                </div>

                <div id="alert-message-spheres" style={{ display: 'none' }}>
                    <p id="alert-message-text">{t('markers.createdSuccess')}</p>
                </div>

                {/* Photo Gallery */}
                {window.viewer && (
                    <ImageGallery
                        isOpen={this.state.isPhotoGalleryOpen}
                        onClose={this.handleClosePhotoGallery}
                        requestId={new URLSearchParams(window.location.search).get('requestID') || ''}
                        apiBaseUrl={window.viewer.API_BASE_URL}
                        apiStage={window.viewer.API_STAGE_PATH}
                        apiPrefix={window.viewer.API_PREFIX}
                        mode={this.state.photoGalleryMode}
                        onPhotosSelected={this.handlePhotosSelected}
                        damagePhotos={this.state.damagePhotosToShow}
                    />
                )}
            </div>
        );

    }

}

class PopupPanel extends React.Component<{ observerData: ObserverData, setProperty: SetProperty }> {
    link: HTMLAnchorElement;

    usdzExporter: any;

    firstTimeRender = true;

    get hasArSupport() {
        return this.props.observerData.runtime.xrSupported || this.usdzExporter;
    }

    hideLeftPanel() {
        const leftPanel = document.getElementById('panel-left');
        if (leftPanel) {
            leftPanel.classList.add('collapsed');
            leftPanel.style.display = 'none';
        }
    }


    constructor(props: any) {
        super(props);
        this.link = (document.getElementById('ar-link') as HTMLAnchorElement);
        if (this.link.relList.supports('ar') || (Boolean(window.webkit?.messageHandlers) && Boolean(/CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(navigator.userAgent)))) {
            this.usdzExporter = new UsdzExporter();
        }

    }

    urlCheckerMarkers = () => {
        const currentUrl = window.location.href;
        let markersActive: boolean;

        // Invertido: Por defecto se muestran, si está el parámetro se ocultan
        if (currentUrl.includes('markersButtons')) {
            markersActive = false; // Si está el parámetro, NO mostrar
        } else {
            markersActive = true; // Por defecto, mostrar
        }
        return markersActive;
    }

    render() {

        let userMode: string;

        const cameraButton = document.getElementById('camera-admin');
        const lightButton = document.getElementById('light-admin');
        const debugButton = document.getElementById('debug-admin');
        const skyboxButton = document.getElementById('skybox-editor');
        const viewButton = document.getElementById('view-editor');
        const markersButton = document.getElementById('markers-div');


        const currentUrl = window.location.href;

        if (this.firstTimeRender) {
            this.firstTimeRender = false;
        }



        if (this.urlCheckerMarkers() === true && markersButton) {
            markersButton.style.display = 'block';
        } else if (markersButton) {
            markersButton.style.display = 'none';
        }



        // Check if toolButtons parameter is present in URL
        const showSliders = !currentUrl.includes('toolButtons');

        //User mode changed by URL parameter 'mode'
        if (currentUrl.includes('editor')) userMode = 'editor';
        else if (currentUrl.includes('admin')) userMode = 'admin';
        else if (currentUrl.includes('viewer')) userMode = 'viewer';
        else userMode = 'viewer'; //default mode


        switch (userMode) {
            case 'editor':
                this.hideLeftPanel();
                return (<div id='popup' className={this.props.observerData.scene.nodes === '[]' ? 'empty' : null}>
                    <img src='/static/Logo_VYSIA_White_Fluor.png' className='logoVE' alt='Vysia Logo' style={{height: '40px', width: 'auto'}} />
                    <PopupPanelControls observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <PopupButtonControls observerData={this.props.observerData} setProperty={this.props.setProperty} userMode={userMode} />
                    {/* PositionPanel removed - now integrated in RightPanel as PositionsMenu */}
                    {/* OrbitSliders removed - now integrated in RightPanel as CameraSliders */}
                    <PositionSphereButtons observerData={this.props.observerData} setProperty={this.props.setProperty} />

                </div>);
                break;
            case 'admin':
                return (<div id='popup' className={this.props.observerData.scene.nodes === '[]' ? 'empty' : null}>
                    <PopupPanelControls observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <PopupButtonControls observerData={this.props.observerData} setProperty={this.props.setProperty} userMode={userMode} />
                    {/* PositionPanel removed - now integrated in RightPanel as PositionsMenu */}
                    {/* OrbitSliders removed - now integrated in RightPanel as CameraSliders */}
                    <PositionSphereButtons observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <Button
                        class='popup-button'
                        id='launch-ar-button'
                        icon='E189'
                        hidden={!this.hasArSupport || this.props.observerData.scene.nodes === '[]'}
                        width={50}
                        height={50}
                        onClick={() => {
                            if (this.usdzExporter) {
                                const sceneRoot = (window as any).viewer.app.root.findByName('sceneRoot');
                                // convert the loaded entity into asdz file
                                this.usdzExporter.build(sceneRoot).then((arrayBuffer: any) => {
                                    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
                                    this.link.href = URL.createObjectURL(blob);
                                    this.link.click();
                                }).catch(console.error);
                            } else {
                                if (window.viewer) window.viewer.xrMode.start();
                            }
                        }}
                    />
                    <div id='floating-buttons-parent'>
                        <Button
                            class={['popup-button', 'large-icon']}
                            id='fullscreen-button'
                            icon='E127'
                            width={50}
                            height={50}
                            onClick={() => {
                                toggleCollapsed();
                            }}
                        />
                    </div>
                </div>);
                break;

            case 'viewer':
                this.hideLeftPanel();

                if (cameraButton) cameraButton.style.display = 'none';
                if (lightButton) lightButton.style.display = 'none';
                if (debugButton) debugButton.style.display = 'none';
                if (skyboxButton) skyboxButton.style.display = 'none';
                if (viewButton) viewButton.style.display = 'none';
                // Eliminado: markersButton ahora se controla solo por URL, igual que otros roles


                return (<div id='popup' className={this.props.observerData.scene.nodes === '[]' ? 'empty' : null}>
                    <img src='/static/Logo_VYSIA_White_Fluor.png' className='logoVE' alt='Vysia Logo' style={{height: '40px', width: 'auto'}} />
                    <PopupPanelControls observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <PopupButtonControls observerData={this.props.observerData} setProperty={this.props.setProperty} userMode={userMode} />
                    {/* PositionPanel removed - now integrated in RightPanel as PositionsMenu */}
                    {/* OrbitSliders removed - now integrated in RightPanel as CameraSliders */}
                    <PositionSphereButtons observerData={this.props.observerData} setProperty={this.props.setProperty} />
                    <Button
                        class='popup-button'
                        id='launch-ar-button'
                        icon='E189'
                        hidden={!this.hasArSupport || this.props.observerData.scene.nodes === '[]'}
                        width={40}
                        height={40}
                        onClick={() => {
                            if (this.usdzExporter) {
                                const sceneRoot = (window as any).viewer.app.root.findByName('sceneRoot');
                                // convert the loaded entity into asdz file
                                this.usdzExporter.build(sceneRoot).then((arrayBuffer: any) => {
                                    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
                                    this.link.href = URL.createObjectURL(blob);
                                    this.link.click();
                                }).catch(console.error);
                            } else {
                                if (window.viewer) window.viewer.xrMode.start();
                            }
                        }}
                    />
                </div>);
            default:

                break;
        }

    }
}

export default PopupPanel;
export { SyncVariables };
